import React, { Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";

import MainLayout from "./layout/MainLayout";
import AuthLayout from "./layout/AuthLayout";
import LoadingSpinner from "./components/UI/loadingSpinner/LoadingSpinner";
import ProtectedRoute from "./route/ProtectedRoute";
import { initializeAuthStore } from "./store/authStore";
import { apiClient } from "./lib/api/api-client";
import "./scss/App.scss";
import AllEventsList from './pages/AllEventsList';

const NotFound = React.lazy(() => import("./pages/NotFound"));
const TicketReports = React.lazy(() => import("./pages/TicketReports"));
const EventAnalyticsDashboard = React.lazy(() => import("./pages/EventAnalyticsDashboard"));
const TicketLookup = React.lazy(() => import("./pages/TicketLookup"));
const Login = React.lazy(() => import("./components/login/Login"));


function App() {
  console.log('App component rendering...');

  // Initialize authentication store on app startup
  useEffect(() => {
    const initializeApp = async () => {
      try {
        console.log('Starting app initialization...');
        
        // Test API connectivity first
        const isConnected = await apiClient.testConnectivity();
        console.log('API connectivity test result:', isConnected);
        
        if (!isConnected) {
          console.warn('API connectivity test failed - app may not work properly');
        }
        
        // Initialize auth store
        initializeAuthStore();
        console.log('App initialization completed');
      } catch (error) {
        console.error('App initialization failed:', error);
      }
    };

    initializeApp();
  }, []);

  // Global error handler to catch any JavaScript errors
  useEffect(() => {
    const handleError = (error: ErrorEvent) => {
      console.error('Global error caught:', error);
      console.error('Error details:', {
        message: error.message,
        filename: error.filename,
        lineno: error.lineno,
        colno: error.colno,
        error: error.error
      });
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      console.error('Unhandled promise rejection:', event.reason);
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  console.log('App component render completed');

  return (
    <BrowserRouter>
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          {/* Protected Routes */}
          <Route 
            element={
              <ProtectedRoute>
                <AuthLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/" element={<MainLayout />}>
              <Route index element={<AllEventsList />} />
              <Route path="/analytics/events" element={<EventAnalyticsDashboard />} />
              <Route path="/reports/tickets" element={<TicketReports />} />
              <Route path="/tickets/lookup" element={<TicketLookup />} />
              <Route path="/events/all" element={<AllEventsList />} />
              <Route path="/events/:eventId/analytics/dashboard" element={<EventAnalyticsDashboard />} />
            </Route>
          </Route>
          
          {/* Public Routes */}
          <Route path="/login" element={<Login />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

export default App;
